import { getJsonFromElement } from '../utils/getJsonFromElement';

/**
 * `#app-config` から読み込んだ設定のモジュールスコープキャッシュ。
 * `useAppConfig()` を複数回呼んでも、DOM パースは初回のみ行う。
 *
 * @type {Record<string, any> | undefined}
 */
let cachedConfig;

/**
 * 共通設定（`#app-config` の JSON）を取得する。
 * 初回のみ DOM から読み込み、以降はキャッシュを返す。
 *
 * @returns {Record<string, any>} 共通設定オブジェクト
 */
function getConfig() {
    if (cachedConfig !== undefined) {
        return cachedConfig;
    }

    cachedConfig = getJsonFromElement('app-config') ?? {};
    return cachedConfig;
}

/**
 * 共通設定を取得する composable。
 *
 * デバッグ判定の優先順位:
 * 1. Vite 環境変数（`import.meta.env`）
 * 2. Blade が埋め込む `app-config.debug`
 * 3. `false`
 *
 * @returns {{
 *   config: Record<string, any>,
 *   debug: boolean,
 *   env: string | undefined,
 *   readonly isProduction: boolean,
 *   readonly isDebug: boolean,
 *   now: string | undefined
 * }} 共通設定値と環境フラグ
 */
export function useAppConfig() {
    const config = getConfig();
    const viteEnv = import.meta.env ?? null;
    const env = viteEnv?.VITE_APP_ENV ?? config.env;
    const viteDebug = viteEnv
        ? (
            viteEnv.DEV === true ||
            env === 'local' ||
            (typeof env === 'string' && env.includes('dev'))
        )
        : null;
    const bladeDebug = config.debug ?? null;
    const debug = viteDebug ?? bladeDebug ?? false;

    return {
        config,
        debug,
        env,
        get isProduction() {
            return this.env === 'production';
        },
        get isDebug() {
            return this.debug;
        },
        now: config.now,
    };
}
